// SourceOriginal — a source's original, in the viewer for its kind
// (`viewerOf`): PDF pages, a Word document's pages, the image, the player,
// the text, or the default (the text that was read and a download). The
// centre of the Knowledge bank's SourceViewer, and the middle of a source
// opened on the Studio desk.
// Pure. `references` number the passages the source's uses cite (none: an
// unmarked original).

export { SourceOriginal };

import { exhaustSwitchCase } from "@bilda/utils";
import type { ReactNode } from "react";
import type { LoadedSource } from "../../app/ports/resource.js";
import {
    type FileKind,
    viewerOf,
} from "../KnowledgeBank/knowledgeBankModel.js";
import type { PageSpan } from "../Navigation/types.js";
import { SourceReading } from "../SourceView/SourceRenderer.js";
import { DefaultViewer } from "./DefaultViewer.js";
import {
    markPassages,
    pdfMarkersOf,
    type Reference,
} from "./sourceViewerModel.js";
import { WordPages } from "./WordPages.js";

const SourceOriginal = ({
    fileKind,
    source,
    focusPages,
    references = [],
    selected,
    onMarker,
    onReupload,
}: {
    readonly fileKind: FileKind;
    readonly source: LoadedSource;
    readonly focusPages?: PageSpan;
    readonly references?: readonly Reference[];
    readonly selected?: number;
    readonly onMarker?: (n: number) => void;
    readonly onReupload?: () => void;
}): ReactNode => {
    const viewer = viewerOf({ fileKind, content: source.content });
    const reading = (projection: LoadedSource["content"]) => (
        <SourceReading
            projection={projection}
            sourceId={source.meta.sourceId}
            annotations={source.annotations}
            focusPages={focusPages}
            onReupload={onReupload}
            markers={pdfMarkersOf(references)}
            selectedMarker={selected}
            onMarker={onMarker}
        />
    );
    const defaultViewer = () => (
        <DefaultViewer
            fileKind={fileKind}
            content={markPassages(source.content, references)}
            original={source.original}
            filename={source.meta.filename}
            renderText={reading}
            onReupload={onReupload}
        />
    );
    switch (viewer) {
        case "image":
        case "audio":
        case "video":
            return reading(source.content);
        // The PDF's "Extracted text" and a text file mark the same passages
        // as their blocks.
        case "pdf":
        case "text":
            return reading(markPassages(source.content, references));
        // A Word document's pages as they are (E-06); if they can't be
        // painted, the default viewer with the text that was read.
        case "word":
            return source.content.type === "word" ? (
                <WordPages
                    data={source.content.data}
                    fallback={defaultViewer()}
                    references={references}
                    selected={selected}
                    onMarker={onMarker}
                />
            ) : (
                defaultViewer()
            );
        case "default":
            return defaultViewer();
        default:
            return exhaustSwitchCase(viewer);
    }
};
